import React, { FC, useContext, useRef } from 'react';

import { Button, ButtonStyles, EmptyStateIcons, Icons, Table } from '@silverfort/sf-components';

import useGetColumns from './columns/useGetColumns';
import useGetMenuItems from './useGetMenuItems';
import IncidentExclusionContext from '../incidentExclusionContext';
import useGetRefSize from '../../../../../hooks/useGetRefSize';

const TABLE_HEADER_HEIGHT = 56;

interface Props {
    setIsCreateExclusionFormOpen: (isOpen: boolean) => void;
}

const ExclusionsTable: FC<Props> = ({ setIsCreateExclusionFormOpen }) => {
    const { updateExclusion, deleteExclusion, exclusions, exclusionsLoading } = useContext(IncidentExclusionContext);
    const columns = useGetColumns(updateExclusion);
    const menuItems = useGetMenuItems(deleteExclusion);
    const tableRef = useRef<HTMLDivElement | null>(null);
    const { height: tableHeight } = useGetRefSize(tableRef);

    const hasExclusions = !exclusionsLoading && exclusions?.length > 0;
    const ROW_HEIGHT_WITH_MARGIN = 56;

    return (
        <div className="flex flex-col w-full h-full">
            <span className="text-sm font-medium text-sf-black mb-4">Exclusion rules</span>

            <div
                className={
                    hasExclusions ? 'w-full flex-shrink-0 overflow-hidden' : 'flex-1 w-full min-h-0 overflow-hidden'
                }
                ref={tableRef}>
                <Table
                    columns={columns}
                    rows={exclusions}
                    bodyHeight={
                        hasExclusions
                            ? ROW_HEIGHT_WITH_MARGIN * exclusions.length
                            : (tableHeight ?? 0) - TABLE_HEADER_HEIGHT
                    }
                    rowClassName="rounded-lg border"
                    keyExtractor={(row) => row.id}
                    loading={exclusionsLoading}
                    menuItems={menuItems}
                    rowMargin={8}
                    tableContainerClassName={hasExclusions ? 'w-full' : 'w-full h-full'}
                    EmptyStateProps={{
                        className: 'w-full h-max text-nowrap',
                        title: 'No custom exclusion rules',
                        description: 'Create rules to exclude expected behaviors in your environment.',
                        icon: EmptyStateIcons.NoItems,
                        actionText: 'Add',
                        actionIcon: Icons.Add,
                        onActionClick: () => setIsCreateExclusionFormOpen(true)
                    }}
                />
            </div>
            {hasExclusions && (
                <div>
                    <Button
                        text="Add"
                        buttonStyle={ButtonStyles.Primary}
                        iconLeft={Icons.Add}
                        onClick={() => setIsCreateExclusionFormOpen(true)}
                    />
                </div>
            )}
        </div>
    );
};

export default ExclusionsTable;
